import React, { useState, useEffect } from "react";
import Sidebar from "../../component/Sidebar";
import Topbar from "../../component/Topbar";
import axios from "axios";
import { baseurl, imgurl } from "../../baseurl";
import { useParams, Link } from "react-router-dom";
import Swal from "sweetalert2";
import { 
  FiArrowLeft, 
  FiSave, 
  FiUser, 
  FiBriefcase, 
  FiMapPin, 
  FiGlobe
} from "react-icons/fi";

// Import the Library
import { State, City } from 'country-state-city';

function VendorProfile() {
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // States for Cascading Dropdowns
  const [statesList, setStatesList] = useState([]);
  const [citiesList, setCitiesList] = useState([]);

  // Form State
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    business_name: "",
    gst_number: "",
    website: "",
    address_line1: "",
    address_line2: "",
    city: "",
    state: "",
    country: "India", // Default
    postal_code: "",
    status: "active",
    profile_image: null
  });

  // Load States for India on component mount
  useEffect(() => {
    const indiaStates = State.getStatesOfCountry('IN');
    setStatesList(indiaStates);
  }, []);

  // Fetch Vendor Profile
  const fetchVendorData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${baseurl}getVendorProfileById/${id}`);
      if (res.data.success) {
        const v = res.data.vendor;
        
        // Find the state code based on the state name coming from DB
        const stateObj = State.getStatesOfCountry('IN').find(s => s.name === v.state);
        if (stateObj) {
          setCitiesList(City.getCitiesOfState('IN', stateObj.isoCode));
        }

        setFormData({
          name: v.name || "",
          email: v.email || "",
          phone: v.phone || "",
          business_name: v.business_name || "",
          gst_number: v.gst_number || "",
          website: v.website || "",
          address_line1: v.address_line1 || "",
          address_line2: v.address_line2 || "",
          city: v.city || "",
          state: v.state || "",
          country: v.country || "India",
          postal_code: v.postal_code || "",
          status: v.status || "active",
          profile_image: v.profile_image
        });
      }
    } catch (err) {
      console.error("Fetch Error:", err);
      Swal.fire("Error", "Failed to fetch vendor details", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) fetchVendorData();
  }, [id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    // Cascading Logic: If state changes, update city list
    if (name === "state") {
      const stateObj = statesList.find(s => s.name === value);
      if (stateObj) {
        const cities = City.getCitiesOfState('IN', stateObj.isoCode);
        setCitiesList(cities);
        setFormData(prev => ({ ...prev, city: "" })); // Reset city when state changes
      } else {
        setCitiesList([]);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put(`${baseurl}updateVendorProfile/${id}`, formData);
      Swal.fire({
        icon: "success",
        title: "Profile Updated",
        text: "Vendor details have been updated successfully.",
        timer: 2000,
        showConfirmButton: false
      });
    } catch (err) {
      Swal.fire("Error", "Failed to update profile", "error");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return (
    <div className="d-flex justify-content-center align-items-center vh-100">
      <div className="spinner-border text-primary"></div>
    </div>
  );

  return (
    <>
      <Topbar />
      <Sidebar />
      <main className="main-wrapper" style={{ backgroundColor: "#f3f4f6", minHeight: "100vh" }}>
        <div className="container-fluid p-4">
          
          <div className="mb-4 d-flex justify-content-between align-items-center">
            <div>
              <Link to="/all-vendor" className="text-decoration-none text-muted small d-flex align-items-center gap-2 mb-2">
                <FiArrowLeft /> Back to Vendors
              </Link>
              <h4 className="fw-bold text-dark">Edit Vendor Profile</h4>
            </div>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="row g-4">
              {/* Left Column */}
              <div className="col-lg-4">
                <div className="card border-0 shadow-sm rounded-4 text-center p-4 mb-4">
                  <div className="mx-auto mb-3">
                    <div className="avatar-placeholder">
                      {formData.profile_image ? (
                         <img src={`${imgurl}${formData.profile_image}`} alt="Profile" className="rounded-circle w-100 h-100" style={{objectFit: 'cover'}} />
                      ) : formData.name?.charAt(0)}
                    </div>
                  </div>
                  <h5 className="fw-bold mb-1">{formData.business_name || "Business Name"}</h5>
                  <p className="text-muted small mb-3">{formData.email}</p>
                  
                  <div className="text-start border-top pt-3">
                    <label className="form-label small fw-bold text-muted">Account Status</label>
                    <select 
                      className={`form-select rounded-3 ${formData.status === 'active' ? 'text-success' : 'text-danger'}`}
                      name="status"
                      value={formData.status}
                      onChange={handleChange}
                    >
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                      <option value="suspended">Suspended</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Right Column */}
              <div className="col-lg-8">
                {/* General & Business */}
                <div className="card border-0 shadow-sm rounded-4 p-4 mb-4">
                  <div className="d-flex align-items-center gap-2 mb-4 text-primary font-bold">
                    <FiBriefcase /> <h6 className="mb-0 fw-bold">Business Information</h6>
                  </div>
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label className="form-label small fw-semibold">Contact Person</label>
                      <input type="text" className="form-control" name="name" value={formData.name} onChange={handleChange} required />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label small fw-semibold">Phone Number</label>
                      <input type="text" className="form-control" name="phone" value={formData.phone} onChange={handleChange} />
                    </div>
                    <div className="col-md-12">
                      <label className="form-label small fw-semibold">Business Name</label>
                      <input type="text" className="form-control" name="business_name" value={formData.business_name} onChange={handleChange} />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label small fw-semibold">GST Number</label>
                      <input type="text" className="form-control" name="gst_number" value={formData.gst_number} onChange={handleChange} />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label small fw-semibold">Website</label>
                      <input type="text" className="form-control" name="website" value={formData.website} onChange={handleChange} placeholder="https://" />
                    </div>
                  </div>
                </div>

                {/* Address Info */}
                <div className="card border-0 shadow-sm rounded-4 p-4 mb-4">
                  <div className="d-flex align-items-center gap-2 mb-4 text-primary">
                    <FiMapPin /> <h6 className="mb-0 fw-bold">Address Details</h6>
                  </div>
                  <div className="row g-3">
                    <div className="col-md-12">
                      <label className="form-label small fw-semibold">Address Line 1</label>
                      <input type="text" className="form-control" name="address_line1" value={formData.address_line1} onChange={handleChange} />
                    </div>
                    
                    <div className="col-md-4">
                      <label className="form-label small fw-semibold">Country</label>
                      <input type="text" className="form-control bg-light" name="country" value={formData.country} readOnly />
                    </div>

                    <div className="col-md-4">
                      <label className="form-label small fw-semibold">State</label>
                      <select className="form-select" name="state" value={formData.state} onChange={handleChange}>
                        <option value="">Select State</option>
                        {statesList.map((state) => (
                          <option key={state.isoCode} value={state.name}>{state.name}</option>
                        ))}
                      </select>
                    </div>

                    <div className="col-md-4">
                      <label className="form-label small fw-semibold">City</label>
                      <select className="form-select" name="city" value={formData.city} onChange={handleChange} disabled={!formData.state}>
                        <option value="">Select City</option>
                        {citiesList.map((city) => (
                          <option key={city.name} value={city.name}>{city.name}</option>
                        ))}
                      </select>
                    </div>

                    <div className="col-md-4">
                      <label className="form-label small fw-semibold">Postal Code</label>
                      <input type="text" className="form-control" name="postal_code" value={formData.postal_code} onChange={handleChange} />
                    </div>
                  </div>
                </div>

                <div className="d-flex justify-content-end mb-5">
                  <button type="submit" className="btn btn-primary btn-lg rounded-pill px-5 shadow" disabled={saving}>
                    {saving ? <span className="spinner-border spinner-border-sm me-2"></span> : <FiSave className="me-2"/>}
                    Update Profile
                  </button>
                </div>
              </div>
            </div>
          </form>
        </div>
      </main>

      <style>{`
        .avatar-placeholder {
          width: 100px;
          height: 100px;
          background: #4f46e5;
          color: white;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 40px;
          font-weight: bold;
          overflow: hidden;
        }
        .form-control:focus, .form-select:focus {
          border-color: #4f46e5;
          box-shadow: 0 0 0 0.25rem rgba(79, 70, 229, 0.1);
        }
      `}</style>
    </>
  );
}

export default VendorProfile;